import "../css/style.css"

import * as THREE from "three"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js"
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader"

import orbitVertexShader from "../assets/orbit/vertex.glsl"
import orbitFragmentShader from "../assets/orbit/fragment.glsl"
import dotsVertexShader from "../assets/dots/vertex.glsl"
import dotsFragmentShader from "../assets/dots/fragment.glsl"
import gsap from "gsap"
import { InteractionManager } from "three.interactive"

class useWebGL {
	constructor() {
		this.canvas = document.querySelector("canvas.planet")
		this.width = this.canvas.clientWidth
		this.height = this.canvas.clientHeight
		this.clock = new THREE.Clock()

		// Variables
		this.earth
		this.laptop
		this.planatRotaitionSpeed = 2
		this.orbitColor = "#e3d7c7"
		this.orbitRadius = 28
		this.orbit1RotationSpeed = 0.004 // Orbit 1
		this.orbit2RotationSpeed = 0.002 // Orbit 2
		this.orbitRotation = false
		this.objectSizeFrom = 10000
		this.beaconColor = new THREE.Color("#d17442")

		this.mouseX = 0
		this.mouseY = 0
		this.targetX = 0
		this.targetY = 0
		this.windowHalfX = window.innerWidth / 2
		this.windowHalfY = window.innerHeight / 2
		this.laptopAnimation = false
		this.planetAnimation = false
		this.planetAnimation2 = false
		this.mobileAnimation = false
		this.vrAnimation = false
		this.tabletAnimation = false
		this.tiltIntensity = 0.17

		// Functions
		this.createScene()
		this.createCamera()
		this.createRenderer()
		this.createLight()
		this.onResize()
		this.createModels()
		this.addEventListeners()
		this.initAnimation()
		this.update()
	}

	createScene() {
		this.scene = new THREE.Scene()
	}

	createLight() {
		this.light = new THREE.HemisphereLight(100, 0xffffff, 10)
		this.light.position.set(0, -20, 0)
		this.scene.add(this.light)
	}

	createCamera() {
		this.camera = new THREE.PerspectiveCamera(75, this.width / this.height, 0.1, 1000)
		this.camera.setViewOffset(window.innerWidth * 1, window.innerHeight, window.innerWidth * -0.165, 0, window.innerWidth, window.innerHeight)
		this.camera.add(new THREE.PointLight("#ffffff", 100), new THREE.AmbientLight("#fff", 1.6))
		this.camera.position.z = 60
		this.scene.add(this.camera)
	}

	createRenderer() {
		this.renderer = new THREE.WebGLRenderer({
			canvas: this.canvas,
			antialias: true,
			alpha: true,
			powerPreference: "high-performance",
		})
		this.renderer.setSize(this.width, this.height)
		this.renderer.setPixelRatio(window.devicePixelRatio || 1)
		this.renderer.physicallyCorrectLights = true
		this.renderer.outputEncoding = THREE.sRGBEncoding
		this.interactionManager = new InteractionManager(this.renderer, this.camera, this.renderer.domElement)
	}

	onResize() {
		this.screen = {
			width: window.innerWidth,
			height: window.innerHeight,
		}
		this.camera.aspect = this.screen.width / this.screen.height
		this.camera.updateProjectionMatrix()
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
		this.renderer.setSize(this.screen.width, this.screen.height)

		if (window.innerWidth < 1023) {
			this.camera.setViewOffset(window.innerWidth * 1, window.innerHeight, window.innerWidth * 0, 0, window.innerWidth, window.innerHeight)
			this.camera.position.z = 90
		} else {
			this.camera.setViewOffset(window.innerWidth * 1, window.innerHeight, window.innerWidth * -0.165, 0, window.innerWidth, window.innerHeight)
		}
	}

	createModels() {
		this.planetgroup = new THREE.Group()
		this.planetgroup.position.set(0, 0, 0)
		this.planetRotateGroup = new THREE.Group()
		this.planetRotateGroup.position.set(0, 0, 0)

		this.laptopgroup = new THREE.Group()
		this.laptopgroup.position.set(0, 0, 0)

		this.mobilegroup = new THREE.Group()
		this.mobilegroup.position.set(0, 0, 0)

		this.vrgroup = new THREE.Group()
		this.vrgroup.position.set(0, 0, 0)

		this.tabletgroup = new THREE.Group()
		this.tabletgroup.position.set(0, 0, 0)

		this.dracoLoader = new DRACOLoader()
		this.dracoLoader.setDecoderPath("https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/js/libs/draco/")
		this.gltfLoader = new GLTFLoader(this.loadingManager)
		this.gltfLoader.setDRACOLoader(this.dracoLoader)

		this.gltfLoader.load("./dist/earth.glb", (earth) => {
			earth.scene.traverse(function (child) {
				if (child.isMesh) {
					child.castShadow = true
					child.receiveShadow = true
					child.geometry.computeVertexNormals() // FIX
				}
			})
			earth.scene.scale.x = 0.3
			earth.scene.scale.z = 0.3
			earth.scene.scale.y = 0.3
			earth.scene.position.set(0, -15, 0)

			this.radians = (23.4 * Math.PI) / 180
			this.earthAxis = new THREE.Vector3(Math.sin(this.radians), Math.cos(this.radians), 0).normalize()
			this.earth = earth.scene
			//this.earth.rotation.set(0, -0.8, (-Math.PI * 13) / 180)

			this.planetgroup.add(this.earth)
			this.planetRotateGroup.add(this.planetgroup)
			this.scene.add(this.planetRotateGroup)
		})

		this.interactionManager.add(this.planetgroup)
		this.planetgroup.addEventListener("mouseover", (event) => {
			this.planetAnimation = true
		})
		this.planetgroup.addEventListener("mouseout", (event) => {
			this.planetAnimation = false
		})

		// Orbits
		this.orbitMaterial = new THREE.ShaderMaterial({
			vertexShader: orbitVertexShader,
			fragmentShader: orbitFragmentShader,
			uniforms: {
				uTime: { value: 0 },
				uWave: { value: false },
				uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
				uFrequency: { value: new THREE.Vector2(10, 4) },
				uColor: { value: new THREE.Color(this.orbitColor) },
				uSize: { value: 10 },
			},
			transparent: true,
			side: THREE.FrontSide,
			blending: THREE.AdditiveBlending,
			depthWrite: false,
		})

		this.points = new THREE.Path().absarc(0, 0, this.orbitRadius, 0, Math.PI * 2).getPoints(360)
		this.orbitGeometry = new THREE.BufferGeometry().setFromPoints(this.points)
		this.orbit = new THREE.Line(this.orbitGeometry, this.orbitMaterial)

		this.orbit.geometry.rotateX(Math.PI * 1.5)
		this.scene.add(this.orbit)

		//2nd Orbit line
		this.orbitGeometry2 = new THREE.BufferGeometry().setFromPoints(this.points)
		this.orbit2 = new THREE.Line(this.orbitGeometry2, this.orbitMaterial)

		this.orbit2.geometry.rotateX(Math.PI * 0.5)
		this.scene.add(this.orbit2)

		// Laptop
		this.gltfLoader.load("./dist/laptop.glb", (laptop) => {
			laptop.scene.traverse(function (child) {
				if (child.isMesh) {
					child.castShadow = true
					child.receiveShadow = true
					child.geometry.computeVertexNormals() // FIX
				}
			})
			laptop.scene.scale.x = 0.2
			laptop.scene.scale.z = 0.2
			laptop.scene.scale.y = 0.2
			laptop.scene.position.set(0, -2.5, 1)

			this.laptop = laptop.scene

			this.laptopgroup.add(this.laptop)
			this.laptopgroup.rotation.set(0, 0, 0)
			this.orbit2.add(this.laptopgroup)
			// this.scene.add(this.laptopgroup)
		})
		this.interactionManager.add(this.laptopgroup)
		this.laptopgroup.addEventListener("mouseover", (event) => {
			this.laptopAnimation = true
		})
		this.laptopgroup.addEventListener("mouseout", (event) => {
			this.laptopAnimation = false
		})

		// Mobile
		this.gltfLoader.load("./dist/mobile.glb", (mobile) => {
			mobile.scene.traverse(function (child) {
				if (child.isMesh) {
					child.castShadow = true
					child.receiveShadow = true
					child.geometry.computeVertexNormals() // FIX
				}
			})
			mobile.scene.scale.x = 2.2
			mobile.scene.scale.z = 2.2
			mobile.scene.scale.y = 2.2
			mobile.scene.position.set(0, 0, 0.3)

			this.mobile = mobile.scene

			this.mobilegroup.add(this.mobile)
			this.mobilegroup.rotation.set(1.5, 0, 1)
			this.orbit.add(this.mobilegroup)
		})
		this.interactionManager.add(this.mobilegroup)

		this.mobilegroup.addEventListener("mouseover", (event) => {
			this.mobileAnimation = true
		})
		this.mobilegroup.addEventListener("mouseout", (event) => {
			this.mobileAnimation = false
		})

		this.gltfLoader.load("./dist/vr.glb", (vr) => {
			vr.scene.traverse(function (child) {
				if (child.isMesh) {
					child.castShadow = true
					child.receiveShadow = true
					child.geometry.computeVertexNormals() // FIX
				}
			})
			vr.scene.scale.x = 1.5
			vr.scene.scale.z = 1.5
			vr.scene.scale.y = 1.5
			vr.scene.position.set(0, 0, 0)

			this.vr = vr.scene

			this.vrgroup.add(this.vr)
			this.vrgroup.rotation.set(1.5, 0, 1)
			this.orbit2.add(this.vrgroup)
		})
		this.interactionManager.add(this.vrgroup)

		this.vrgroup.addEventListener("mouseover", (event) => {
			this.vrAnimation = true
		})
		this.vrgroup.addEventListener("mouseout", (event) => {
			this.vrAnimation = false
		})

		this.gltfLoader.load("./dist/tablet.glb", (tablet) => {
			tablet.scene.traverse(function (child) {
				if (child.isMesh) {
					child.castShadow = true
					child.receiveShadow = true
					child.geometry.computeVertexNormals() // FIX
				}
			})
			tablet.scene.scale.x = 26
			tablet.scene.scale.z = 26
			tablet.scene.scale.y = 26
			tablet.scene.position.set(0, 0, -0.5)

			this.tablet = tablet.scene

			this.tabletgroup.add(this.tablet)
			this.tabletgroup.rotation.set(5.5, 0, 0)
			this.orbit.add(this.tabletgroup)
		})

		this.interactionManager.add(this.tabletgroup)

		this.tabletgroup.addEventListener("mouseover", (event) => {
			this.tabletAnimation = true
		})
		this.tabletgroup.addEventListener("mouseout", (event) => {
			this.tabletAnimation = false
		})

		this.customGeometry = new THREE.BufferGeometry()
		this.miniplenetsCount = 1
		this.positionArray = new Float32Array(this.miniplenetsCount * 3)
		this.scaleArray = new Float32Array(this.miniplenetsCount)
		for (let i = 0; i < this.miniplenetsCount; i++) {
			this.scaleArray[i] = 1
		}
		this.customGeometry.setAttribute("position", new THREE.BufferAttribute(this.positionArray, 3))
		this.customGeometry.setAttribute("aScale", new THREE.BufferAttribute(this.scaleArray, 1))
		this.customMaterial = new THREE.ShaderMaterial({
			uniforms: {
				uTime: { value: 0 },
				uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
				uSize: { value: this.objectSizeFrom },
				uColor: { value: new THREE.Color(this.beaconColor) },
			},
			vertexShader: dotsVertexShader,
			fragmentShader: dotsFragmentShader,
			transparent: true,
			blending: THREE.AdditiveBlending,
			//depthWrite: false,
		})

		this.glow = new THREE.Points(this.customGeometry, this.customMaterial)
		this.glow2 = this.glow.clone()
		this.glow3 = this.glow.clone()
		this.glow4 = this.glow.clone()

		this.orbit.add(this.glow, this.glow2)
		this.orbit2.add(this.glow3, this.glow4)
	}

	addEventListeners() {
		window.addEventListener("resize", this.onResize.bind(this))

		document.getElementById("canvas").addEventListener("mousemove", (event) => {
			this.mouseX = event.clientX - this.windowHalfX
			this.mouseY = event.clientY - this.windowHalfY
		})
	}

	initAnimation() {
		gsap.to(this.orbit.rotation, {
			duration: 8,
			delay: 5,
			ease: "back.out(1)",
			x: Math.PI / 9,
			y: Math.PI / 5,
			z: Math.PI / 5,
		})
		gsap.to(this.orbit.position, {
			duration: 8,
			delay: 5,
			ease: "back.out(1)",
			x: -3,
			y: 4,
			z: 0,
		})

		gsap.to(this.orbit2.rotation, {
			duration: 8,
			delay: 5,
			x: -Math.PI / 9,
			y: 0.4,
			z: -Math.PI / 6,
		})
		gsap.to(this.orbit2.position, {
			duration: 8,
			delay: 5,
			x: -3,
			y: -4,
			z: 0,
		})
		setTimeout(() => {
			this.orbitRotation = true
		}, 5100)
	}

	update() {
		this.elapsedTime = this.clock.getElapsedTime() / 2
		this.elapsedTime3 = this.elapsedTime + 2
		this.elapsedTime4 = this.elapsedTime + 3.5
		this.elapsedTime6 = this.elapsedTime + 5
		if (this.planetAnimation == false) {
			this.planetgroup.rotateY(0.005)
		}

		// orbit rotation
		if (this.orbitRotation) {
			this.orbit.rotateZ(this.orbit1RotationSpeed)
			this.orbit2.rotateX(this.orbit2RotationSpeed)
		}

		this.glow4.position.set(Math.cos(this.elapsedTime), 0, Math.sin(this.elapsedTime)).multiplyScalar(this.orbitRadius)
		this.laptopgroup.position.set(Math.cos(this.elapsedTime), 0, Math.sin(this.elapsedTime)).multiplyScalar(this.orbitRadius)
		this.laptopgroup.rotation.y = -Math.cos(this.elapsedTime)
		this.laptopgroup.rotation.x = Math.sin(this.elapsedTime)
		if (this.laptopAnimation) {
			this.laptop.rotation.y += 0.02
			this.laptop.rotation.x += 0.02
		}
		this.glow3.position.set(Math.cos(this.elapsedTime4), 0, Math.sin(this.elapsedTime4)).multiplyScalar(this.orbitRadius)
		this.vrgroup.position.set(Math.cos(this.elapsedTime4), 0, Math.sin(this.elapsedTime4)).multiplyScalar(this.orbitRadius)
		this.vrgroup.rotation.y = Math.cos(this.elapsedTime4)
		this.vrgroup.rotation.x = Math.sin(-this.elapsedTime4)
		if (this.vrAnimation) {
			this.vr.rotation.y += 0.02
			this.vr.rotation.x += 0.02
		}
		this.createMouse()
		this.glow2.position.set(Math.cos(this.elapsedTime3), 0, Math.sin(this.elapsedTime3)).multiplyScalar(this.orbitRadius)
		this.mobilegroup.position.set(Math.cos(this.elapsedTime3), 0, Math.sin(this.elapsedTime3)).multiplyScalar(this.orbitRadius)
		this.mobilegroup.rotation.y = (-Math.cos(this.elapsedTime3) * Math.PI) / 8
		this.mobilegroup.rotation.z = (Math.sin(this.elapsedTime3) * Math.PI) / 3
		if (this.mobileAnimation) {
			this.mobile.rotation.y += 0.02
			this.mobile.rotation.x += 0.02
		}

		this.glow.position.set(Math.cos(this.elapsedTime6), 0, Math.sin(this.elapsedTime6)).multiplyScalar(this.orbitRadius)
		this.tabletgroup.position.set(Math.cos(this.elapsedTime6), 0, Math.sin(this.elapsedTime6)).multiplyScalar(this.orbitRadius)
		this.tabletgroup.rotation.y = (-Math.cos(this.elapsedTime6) * Math.PI) / 8
		this.tabletgroup.rotation.z = (Math.sin(this.elapsedTime6) * Math.PI) / 3
		if (this.tabletAnimation) {
			//this.tablet.rotation.y += 0.02
			this.tablet.rotation.x += 0.02
		}
		this.interactionManager.update()
		this.renderer.render(this.scene, this.camera)
		window.requestAnimationFrame(this.update.bind(this))
	}

	createMouse() {
		if (this.planetAnimation) {
			this.targetX = this.mouseX * 0.0006
			this.targetY = this.mouseY * 0.0006
			this.planetRotateGroup.rotation.y += this.tiltIntensity * (this.targetX - this.planetRotateGroup.rotation.y)
			this.planetRotateGroup.rotation.x += this.tiltIntensity * (this.targetY - this.planetRotateGroup.rotation.x)
		}
	}
}

new useWebGL()
